import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, LogOut, User } from 'lucide-react';
import { useI18n } from '../../i18n/useI18n';
import { authService } from '../../services/authService';

const PATH_META = {
  '/': { crumbKey: 'nav.pilotage', titleKey: 'pages.dashboard.title' },
  '/members': { crumbKey: 'nav.gestion', titleKey: 'pages.members.title' },
  '/finance': { crumbKey: 'nav.gestion', titleKey: 'pages.finance.title' },
  '/rewards': { crumbKey: 'nav.gestion', titleKey: 'pages.rewards.title' },
  '/library': { crumbKey: 'nav.gestion', titleKey: 'pages.library.title' },
  '/administrators': { crumbKey: 'nav.gestion', titleKey: 'pages.administrators.title' },
  '/boutique': { crumbKey: 'nav.ecosysteme', titleKey: 'pages.boutique.title' },
  '/marketplace': { crumbKey: 'nav.ecosysteme', titleKey: 'pages.marketplace.title' },
  '/community': { crumbKey: 'nav.ecosysteme', titleKey: 'pages.community.title' },
  '/community/support': { crumbKey: 'nav.ecosysteme', title: 'Support' },
  '/communication': { crumbKey: 'nav.ecosysteme', titleKey: 'pages.communication.title' },
  '/security': { crumbKey: 'nav.systeme', titleKey: 'pages.security.title' },
  '/settings': { crumbKey: 'nav.systeme', titleKey: 'pages.settings.title' },
};

export default function Header({ onOpenMobileMenu }) {
  const location = useLocation();
  const { t } = useI18n();
  const meta = PATH_META[location.pathname] || { crumbKey: 'nav.pilotage', titleKey: 'pages.dashboard.title' };

  const user = useMemo(() => authService.getCurrentUser(), []);
  const label = useMemo(() => {
    if (!user || typeof user !== 'object') return t('common.profile');
    // Uniquement des champs présents dans le contrat /admin/me.administrateur
    if (user.email) return String(user.email);
    if (user.niveau) return String(user.niveau);
    if (user.id != null) return `Admin #${user.id}`;
    return t('common.profile');
  }, [user, t]);

  const niveauHint = user?.niveau ? String(user.niveau) : null;

  const handleLogout = () => {
    authService.logout();
    window.location.href = '/login';
  };

  return (
    <header
      className="sticky top-0 z-30 px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3 backdrop-blur-md"
      style={{
        background: 'var(--header-bg)',
        borderBottom: '1px solid var(--border)',
        color: 'var(--text-primary)',
      }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={onOpenMobileMenu}
          className="lg:hidden p-2 rounded-xl"
          style={{ border: '1px solid var(--border)' }}
        >
          <Menu size={18} />
        </button>
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
            {t(meta.crumbKey)}
          </p>
          <h2 className="text-sm sm:text-base font-bold truncate" style={{ color: 'var(--text-primary)' }}>
            {meta.title ?? t(meta.titleKey)}
          </h2>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div
          className="hidden sm:flex items-center gap-2 rounded-xl px-3 py-1.5 text-xs font-semibold max-w-[220px]"
          style={{ border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
          title={niveauHint ? `Niveau : ${niveauHint}` : undefined}
        >
          <User size={14} className="shrink-0" />
          <span className="truncate">{label}</span>
        </div>
        <button
          onClick={handleLogout}
          className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold transition"
          style={{ border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
          title={t('common.logout')}
        >
          <LogOut size={14} />
          <span className="hidden sm:inline">{t('common.logout')}</span>
        </button>
      </div>
    </header>
  );
}